import type { ProjectAssetsModel } from '@ve-pro/api/aside/type'
import { SegmentType } from '@ve-pro/enums/segment'
import { TrackType } from '@ve-pro/enums/track-type'
import { useTrackStore } from '@ve-pro/store'
import type {
  SegmentCollection,
  SegmentFragment,
  SegmentTransition,
  SegmentVideo,
  Track,
} from '@ve-pro/types/track.type'
import { removeSegment, remove } from '../tracks-actions'
import { durationToFrames, framesToDuration, getFullFrameMs } from '../frame'

// 转场默认时长
export const TRANSITION_DURATION = framesToDuration(14) // 14f 466ms
// 转场最小时长
export const TRANSITION_DURATION_MIN = framesToDuration(2) // 2f 66ms
// 转场最大时长
export const TRANSITION_DURATION_MAX = 5000 // 5s

export type CurrentTimeSegmentPositionRelative = 'left' | 'right'

export type SupportTransitionSegment = SegmentFragment | SegmentVideo
/**
 * 将时长转换为有效的double帧数 时长
 * @param duration 输入时长
 * @returns double帧数 时长
 */
export function resolveSegmentTransitionDuration(
  duration: SegmentTransition['duration'],
): SegmentTransition['duration'] {
  const frames = Math.floor(durationToFrames(duration) / 2) * 2
  return framesToDuration(frames)
}

/**
 * 获取当前时间对应主轨道元件和其相对位置信息
 * @returns
 */
export function getCurrentTimeSegmentPositionRelative(): {
  segment: SupportTransitionSegment
  position: CurrentTimeSegmentPositionRelative
} | null {
  const trackStore = useTrackStore()

  const segment = trackStore.mainTack?.segments.find(
    (seg): seg is SupportTransitionSegment => {
      return (
        isSupportTransition(seg) &&
        trackStore.currentTime >= seg.start_time &&
        trackStore.currentTime <= Math.floor(seg.start_time + seg.duration)
      )
    },
  )

  if (segment) {
    const position: CurrentTimeSegmentPositionRelative =
      trackStore.currentTime <= segment.start_time + segment.duration / 2
        ? 'left'
        : 'right'
    return { segment: segment, position }
  }

  return null
}

export function getTransition(segment: SupportTransitionSegment): {
  before: SegmentTransition | null
  behind: SegmentTransition | null
} {
  const before = segment.before_transition_id
    ? getTransitionById(segment.before_transition_id)
    : null

  const behind = segment.behind_transition_id
    ? getTransitionById(segment.behind_transition_id)
    : null
  return { before, behind }
}

/**
 * 更新视频元件的转场
 * @param segment
 * @param remove 是否删除无效的转场
 */
export function updateVideoSegmentTransition(
  segment: SupportTransitionSegment,
  remove = false,
): void {
  const { before, behind } = getTransition(segment)

  if (before) {
    updateTransition(before, remove)
  }

  if (behind) {
    updateTransition(behind, remove)
  }
}

/**
 * 更新转场
 * @param transition
 * @param remove 是否删除无效的转场
 */
export function updateTransition(
  transition: SegmentTransition,
  remove = true,
): void {
  const max = getTransitionDurationMax(transition)
  const duration = Math.min(transition.duration, max)
  if (duration < TRANSITION_DURATION_MIN) {
    transition.visible = false
    if (remove) {
      removeTransition(transition)
    }
  } else {
    transition.visible = true
    transition.duration = resolveSegmentTransitionDuration(duration)
  }
}

// 有时候并不需要马上就更新主轨道，而是统一修改后在更新
export function removeTransitionNotUpdate(transition: SegmentTransition): void {
  const { before, behind } = transition
  if (before.behind_transition_id === transition.id) {
    before.behind_transition_id = null
  }
  if (behind.before_transition_id === transition.id) {
    behind.before_transition_id = null
  }

  const trackStore = useTrackStore()
  const track = trackStore.tracks.find((t) => t.id === transition.track_id)
  if (track && track.type === TrackType.Video) {
    remove(transition, track.segments)
  }
}

export function removeTransition(transition: SegmentTransition): void {
  const { before, behind } = transition
  if (before.behind_transition_id === transition.id) {
    before.behind_transition_id = null
  }
  if (behind.before_transition_id === transition.id) {
    behind.before_transition_id = null
  }

  removeSegment(transition)
}

export function getTransitionById(
  id: SegmentTransition['id'],
): SegmentTransition | null {
  const trackStore = useTrackStore()
  const target = trackStore.segments.find((t) => t.id === id)

  return target && isTransition(target) ? target : null
}

/**
 * 获取转场的最大时长
 * @param transition
 * @returns
 */
export function getTransitionDurationMax(
  transition: SegmentTransition,
): number {
  const { before, behind } = transition

  const shorterDurationFrames = durationToFrames(
    getFullFrameMs(Math.min(before.duration, behind.duration), {
      rounding: 'floor',
    }),
  )

  const duration = Math.min(
    TRANSITION_DURATION_MAX,
    // 可能这个逻辑有点粗暴
    Math.max(
      TRANSITION_DURATION_MIN,
      // 需求：叠加转场还是非叠加转场的最大时长应该是这段视频的一半
      resolveSegmentTransitionDuration(
        framesToDuration(shorterDurationFrames / 2),
      ),
      // transition.overlap ? Math.floor(shorterDuration / 2) : shorterDuration,
    ),
  )
  return duration
}

/**
 * 获取转场视频的最小时长
 * @param transition
 * @returns
 */
export function getTransitionVideoDurationMin(): number {
  return TRANSITION_DURATION_MIN * 2
}
// 根据是否重叠来获取最小时长
// export function getTransitionVideoDurationMin(overlap: boolean): number {
//   return (
//     TRANSITION_DURATION_MIN / 2 +
//     (overlap ? TRANSITION_DURATION_MIN : TRANSITION_DURATION_MIN / 2)
//   )
// }

/**
 * 获取转场是否重叠
 * @param material
 * @returns true 叠加 false 不叠加
 */
export function getTransitionOverlap(material: ProjectAssetsModel): boolean {
  // 转场.zip包， 上传完后端会解压，
  // 素材数据有file_urls: [ xxxx, xxxx, xxxx.1.mws ] ，
  // 通过匹配.mws(或.json)后缀的url，1为叠加，2为不叠加(xxxx.2.mws)
  function isOverlap(url: string) {
    try {
      const _url = url.split('?')[0] ?? ''
      return /.1.(mws|json)$/.test(_url)
    } catch (e) {
      //
    }
    return false
  }
  if (material.file_urls && material.file_urls.some((url) => isOverlap(url))) {
    return true
  }
  return false
}

export function isTransition(
  segment: SegmentCollection,
): segment is SegmentTransition {
  return segment.type === SegmentType.Transition
}

/**
 * 获取时长：原有时长减去了转场重叠时长后的时长
 * @param segment
 * @returns 除去转场后的时长
 */
export function getExcludeTransitionDuration(
  segment: SegmentCollection,
): number {
  let duration = segment.duration

  if (segment.type === SegmentType.Video && segment.behind_transition_id) {
    // 后转场（影响下一个元件的入场时间）
    const behind_transition = getTransitionById(segment.behind_transition_id)

    if (behind_transition && behind_transition.overlap) {
      const value = behind_transition.duration
      duration = Math.floor(duration - value)
    }
  }
  return duration
}
export function getExcludeTransitionDurationFrames(
  segment: SegmentCollection,
): number {
  let frames = segment.duration_frames

  if (segment.type === SegmentType.Video && segment.behind_transition_id) {
    // 后转场（影响下一个元件的入场时间）
    const behind_transition = getTransitionById(segment.behind_transition_id)

    if (behind_transition && behind_transition.overlap) {
      const value = behind_transition.duration_frames
      frames = Math.floor(frames - value)
    }
  }
  return frames
}

// 转场分割
export function splitSegmentTransition(
  segment: SegmentCollection,
  side: 'left' | 'right',
): void {
  if (segment.type !== SegmentType.Video) {
    return
  }

  const { before, behind } = getTransition(segment)
  if (side === 'left') {
    segment.behind_transition_id = null
    before && updateTransition(before)
  } else {
    segment.before_transition_id = null
    if (behind) {
      behind.before = segment
      updateTransition(behind)
    }
  }
}

/**
 * 更新转场的关联性
 * 背景：在数据的copy过程中，会将转场关联的视频元件的对象的指针也做了copy，导致前后两个相同的视频元件不是同个对象指针
 * @param tracks
 */
export function updateTransitionRelevance(mainTrack?: Track | null): void {
  const trackStore = useTrackStore()
  mainTrack = mainTrack ?? trackStore.mainTack

  mainTrack?.segments.forEach((segment) => {
    if (isSupportTransition(segment)) {
      const { before, behind } = getTransition(segment)

      if (before) {
        before.behind = segment
      }

      if (behind) {
        behind.before = segment
      }
    }
  })
}

export function isSupportTransition(
  segment: SegmentCollection,
): segment is SupportTransitionSegment {
  return (
    segment.type === SegmentType.Video || segment.type === SegmentType.Fragment
  )
}
